// Phase 8.4 — Stripe Checkout success page
// Polls /api/checkout/stripe/status/{session_id} every 2s until paid/expired.

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Package } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const MAX_POLLS = 12;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [err, setErr] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/stripe/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        setAttempts(n);
        if (data.payment_status === "paid" || data.status === "expired") return;
        if (n < MAX_POLLS) {
          setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="checkout-success-no-session">
        <XCircle className="h-10 w-10 mx-auto text-rose-300" />
        <h1 className="font-anton text-3xl uppercase mt-3">Missing session</h1>
        <p className="text-lifted-dim mt-2">No session_id was provided.</p>
        <Link to="/shop" className="btn-lifted mt-4 inline-flex">Back to shop</Link>
      </div>
    );
  }

  const paid = status.payment_status === "paid";
  const expired = status.status === "expired";

  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="checkout-success">
      {paid ? (
        <>
          <CheckCircle2 className="h-12 w-12 mx-auto text-lifted-green" />
          <h1 className="font-anton text-3xl uppercase mt-3">Payment received</h1>
          <p className="text-lifted-dim mt-2">
            Order <b>{status.order_id}</b> is now processing. You'll get an email
            when it ships.
          </p>
          <div className="flex gap-2 justify-center mt-6">
            <Link to="/account" className="btn-lifted inline-flex items-center gap-2" data-testid="checkout-success-account">
              <Package size={14} /> Track your order
            </Link>
            <Link to="/shop" className="btn-ghost" data-testid="checkout-success-shop">Continue shopping</Link>
          </div>
        </>
      ) : expired ? (
        <>
          <XCircle className="h-10 w-10 mx-auto text-amber-300" />
          <h1 className="font-anton text-3xl uppercase mt-3">Session expired</h1>
          <p className="text-lifted-dim mt-2">Please return to your cart and try again.</p>
          <Link to="/cart" className="btn-lifted mt-4 inline-flex">Back to cart</Link>
        </>
      ) : (
        <>
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-lifted-green" />
          <h1 className="font-anton text-3xl uppercase mt-3">Confirming payment…</h1>
          <p className="text-lifted-dim mt-2">
            Checking Stripe ({attempts + 1}/{MAX_POLLS + 1})…
          </p>
          {err && <p className="text-rose-300 text-sm mt-2">{err}</p>}
        </>
      )}
    </div>
  );
}
